import { z } from 'zod';
import { GrantStatusSchema, ScopePackageStatusSchema, SubQuoteKindSchema, SubQuoteStatusSchema, VettingRequestStatusSchema, } from '../lib/enums';
import { zDateOnly, zEmail, zIsoDateTime, zJurisdiction, zMoneyNonNeg, zQty, zRateNonNeg, zUuid, } from './common';
import { FileRefSchema } from './files';
/**
 * Sharing seam — the ONLY sanctioned cross-tenant exchange. Structural laws
 * these shapes enforce:
 * - scope packages carry ZERO client/pricing data (scope text is COPIED at
 *   creation; no estimate/client references appear in sub-facing DTOs);
 * - sub quotes expose the PRICE to the contractor, never the sub's cost
 *   breakdown, and have no FKs into the sub's libraries;
 * - grants are per-counterparty, nothing transitive;
 * - vetting data is stored and displayed, NEVER verified by the product.
 */
// ------------------------------------------------------------ scope packages
export const ScopePackageSchema = z.object({
    id: zUuid,
    estimateId: zUuid,
    sourceSectionId: zUuid.nullable(),
    title: z.string().min(1).max(300),
    scopeDescription: z.string().max(50000),
    dueDate: zDateOnly.nullable(),
    questions: z.array(z.string().max(1000)),
    status: ScopePackageStatusSchema,
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateScopePackageRequestSchema = z
    .object({
    /** When set, the section's scope text is COPIED into the package. */
    sourceSectionId: zUuid.nullable().optional(),
    title: z.string().min(1).max(300),
    scopeDescription: z.string().max(50000).optional(),
    dueDate: zDateOnly.nullable().optional(),
    questions: z.array(z.string().min(1).max(1000)).max(30).optional(),
})
    .strict();
export const UpdateScopePackageRequestSchema = z
    .object({
    title: z.string().min(1).max(300).optional(),
    scopeDescription: z.string().max(50000).optional(),
    dueDate: zDateOnly.nullable().optional(),
    questions: z.array(z.string().min(1).max(1000)).max(30).optional(),
    status: z.enum(['closed', 'cancelled']).optional(),
})
    .strict();
// --------------------------------------------------------------------- grants
export const ScopePackageGrantSchema = z.object({
    id: zUuid,
    scopePackageId: zUuid,
    granteeCompanyId: zUuid.nullable(),
    /** Via company_directory barrier view; null while email invite is pending. */
    granteeCompanyName: z.string().nullable(),
    inviteEmail: zEmail.nullable(),
    status: GrantStatusSchema,
    createdAt: zIsoDateTime,
});
/** Invite an existing company (via directory) OR an email address. */
export const CreateInviteRequestSchema = z.union([
    z.object({ companyId: zUuid }).strict(),
    z.object({ email: zEmail }).strict(),
]);
/** Row of the `company_directory` SECURITY BARRIER view. */
export const CompanyDirectoryEntrySchema = z.object({
    companyId: zUuid,
    name: z.string(),
    trades: z.array(z.string()),
    isSubcontractor: z.boolean(),
});
export const DirectorySearchQuerySchema = z.object({
    q: z.string().max(200).optional(),
    trade: z.string().max(64).optional(),
});
// ------------------------------------------------------------------ sub quotes
/** A sub quote line: price to the contractor. No cost breakdown, ever. */
export const SubQuoteLineSchema = z.object({
    id: zUuid,
    description: z.string().min(1).max(1000),
    qty: zQty,
    unit: z.string().max(20).nullable(),
    unitPrice: zRateNonNeg,
});
const SubQuoteLineWriteSchema = z
    .object({
    description: z.string().min(1).max(1000),
    qty: zQty,
    unit: z.string().max(20).nullable().optional(),
    unitPrice: zRateNonNeg,
})
    .strict();
export const SubQuoteSchema = z.object({
    id: zUuid,
    scopePackageId: zUuid,
    status: SubQuoteStatusSchema,
    kind: SubQuoteKindSchema,
    lumpSumAmount: zMoneyNonNeg.nullable(),
    lines: z.array(SubQuoteLineSchema),
    clarifications: z.string().max(20000).nullable(),
    exclusions: z.string().max(20000).nullable(),
    /** Derived total (lump sum, or Σ line qty × unitPrice rounded per line). */
    total: zMoneyNonNeg.nullable(),
    submittedAt: zIsoDateTime.nullable(),
    updatedAt: zIsoDateTime,
});
/** PUT /sub/scope-packages/:id/quote — draft upsert (revisable until accepted/due). */
export const PutSubQuoteRequestSchema = z
    .object({
    kind: SubQuoteKindSchema,
    lumpSumAmount: zMoneyNonNeg.nullable().optional(),
    lines: z.array(SubQuoteLineWriteSchema).max(200).optional(),
    clarifications: z.string().max(20000).nullable().optional(),
    exclusions: z.string().max(20000).nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.kind === 'lump_sum' && v.lumpSumAmount == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'lumpSumAmount is required for lump_sum quotes',
            path: ['lumpSumAmount'],
        });
    }
    if (v.kind === 'line_items' && (v.lines === undefined || v.lines.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'at least one line is required for line_items quotes',
            path: ['lines'],
        });
    }
});
/** What the sub sees in their inbox — package scope, no client/pricing data. */
export const SubInboxItemSchema = z.object({
    packageId: zUuid,
    contractorCompanyName: z.string(),
    title: z.string(),
    scopeDescription: z.string(),
    dueDate: zDateOnly.nullable(),
    questions: z.array(z.string()),
    packageStatus: ScopePackageStatusSchema,
    grantStatus: GrantStatusSchema,
    attachments: z.array(FileRefSchema),
    myQuoteStatus: SubQuoteStatusSchema.nullable(),
    vettingRequested: z.boolean(),
});
export const SubInboxResponseSchema = z.array(SubInboxItemSchema);
// --------------------------------------------------------------- owner's view
/** Contractor's compare view — submitted+ quotes only (drafts invisible). */
export const SubQuoteCompareSchema = z.object({
    id: zUuid,
    subCompanyId: zUuid,
    subCompanyName: z.string(),
    status: SubQuoteStatusSchema.exclude(['draft']),
    kind: SubQuoteKindSchema,
    lumpSumAmount: zMoneyNonNeg.nullable(),
    lines: z.array(SubQuoteLineSchema),
    clarifications: z.string().nullable(),
    exclusions: z.string().nullable(),
    total: zMoneyNonNeg,
    submittedAt: zIsoDateTime,
    vetting: z
        .object({
        status: VettingRequestStatusSchema,
        submittedAt: zIsoDateTime.nullable(),
    })
        .nullable(),
});
export const ScopePackageQuotesResponseSchema = z.object({
    packageId: zUuid,
    quotes: z.array(SubQuoteCompareSchema),
});
export const ScopePackageDetailResponseSchema = z.object({
    package: ScopePackageSchema,
    grants: z.array(ScopePackageGrantSchema),
    attachments: z.array(FileRefSchema),
});
/**
 * Accept roll-up (one tx): quote → accepted; lines snapshotted into a new
 * `sub_rollup` section with `subquote` lines at the sub's PRICE as the
 * contractor's COST; contractor markup layers on top via the engine.
 */
export const AcceptSubQuoteResponseSchema = z.object({
    quoteId: zUuid,
    estimateId: zUuid,
    createdSectionId: zUuid,
});
export const DeclineSubQuoteRequestSchema = z
    .object({
    /** Included in the courteous decline outbox email. */
    message: z.string().max(2000).optional(),
})
    .strict();
// -------------------------------------------------------------------- vetting
export const CreateVettingRequestSchema = z
    .object({
    message: z.string().max(2000).optional(),
})
    .strict();
/**
 * Sub's vetting submission — license/COI/attestations are stored and
 * displayed with an "as-entered, not verified" boundary; the product never
 * verifies any of it.
 */
export const VettingSubmissionSchema = z
    .object({
    license: z
        .object({
        jurisdiction: zJurisdiction,
        trade: z.string().max(120).nullable().optional(),
        licenseNumber: z.string().min(1).max(120),
        classification: z.string().max(120).nullable().optional(),
        authority: z.string().max(200).nullable().optional(),
        expiresAt: zDateOnly.nullable().optional(),
    })
        .strict()
        .nullable(),
    coiFileId: zUuid.nullable(),
    attestations: z.array(z
        .object({
        key: z.string().min(1).max(64),
        label: z.string().min(1).max(300),
        affirmed: z.boolean(),
    })
        .strict()),
})
    .strict();
export const VettingStateResponseSchema = z.object({
    scopePackageId: zUuid,
    status: VettingRequestStatusSchema,
    requestMessage: z.string().nullable(),
    submission: VettingSubmissionSchema.nullable(),
    submittedAt: zIsoDateTime.nullable(),
});
//# sourceMappingURL=seam.js.map